import { useCallback } from 'react';

import {
  useAllZoomRecordingsQuery,
  AllZoomRecordingsQueryVariables,
  ZoomRecordingFieldsFragment,
} from 'core/graphql';

type ZoomRecordingFilters = Omit<
  AllZoomRecordingsQueryVariables,
  'first' | 'after'
>;

interface IZoomRecordingsHook {
  data: ZoomRecordingFieldsFragment[];
  loading: boolean;
  error?: string;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/** Relay-cursor paginated list of raw ZoomRecording rows (incl. orphans). */
export const useZoomRecordings = (
  filters: ZoomRecordingFilters = {},
  pageSize = 50,
): IZoomRecordingsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = useAllZoomRecordingsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.allZoomRecordings;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.allZoomRecordings) {
          return prev;
        }

        return {
          allZoomRecordings: {
            ...fetchMoreResult.allZoomRecordings,
            edges: [
              ...(prev.allZoomRecordings?.edges ?? []),
              ...fetchMoreResult.allZoomRecordings.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is ZoomRecordingFieldsFragment => Boolean(node)),
    loading,
    error: error?.message,
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
